import { useState, useEffect, useContext } from 'react';
import { Container, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { useAppDispatch } from 'store/hooks';
import { getAllRequestsForAdmin } from 'store/slices/accountManagementSlice';
import COLORS from 'theme/colors';
import { useNavigate } from 'react-router-dom';
import { DateFilter } from 'utils/gridOperators';
import { ROUTE } from 'route/RouteEnums';
import CustomDataGrid from 'utils/customDataGrid';
import { AuthContext } from 'utils/AuthContext';

const AccountManagementRequest = () => {
  const pageKey = 'account-mgmt-dashboard';
  const { user, setUserDetails } = useContext(AuthContext);
  const [requests, setRequests] = useState<any[] | null>([]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const getRequestList = async () => {
    const requestDetails: any = [];
    await dispatch(getAllRequestsForAdmin({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          if (response?.data?.length) {
            // eslint-disable-next-line array-callback-return
            response?.data?.map((d: any) => {
              const data = {
                id: d?.id,
                ticketId: d?.ticketId,
                patient: d?.patient,
                patientName:
                  d?.patientFirstName?.charAt(0).toUpperCase() +
                  d?.patientFirstName?.slice(1) +
                  ' ' +
                  d?.patientLastName?.charAt(0).toUpperCase() +
                  d?.patientLastName?.slice(1),
                guardianName:
                  d?.guardianFirstName?.charAt(0).toUpperCase() +
                  d?.guardianFirstName?.slice(1) +
                  ' ' +
                  d?.guardianLastName?.charAt(0).toUpperCase() +
                  d?.guardianLastName?.slice(1),
                type: d?.ticketType,
                requestDate: d?.dateOfRequest,
                requestType: d?.requestType,
                status: d?.status,
              };

              requestDetails.push(data);

              setRequests(requestDetails);
            });
          } else {
            setLoading(false);
          }
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    getRequestList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPatientChart = (patientId: string | number) => {
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`, '_blank');
  };

  const columns: GridColDef[] = [
    {
      field: 'patientName',
      headerName: 'Patient Name',
      width: 200,
      type: 'string',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {params?.row?.patient && params?.row?.patientName && (
              <Tooltip title='Patient Chart'>
                <IconButton
                  size='small'
                  aria-label='Patient Chart'
                  onClick={(event) => {
                    event.stopPropagation();
                    openPatientChart(params.row.patient);
                  }}
                >
                  <PersonIcon fontSize='small' sx={{ color: '#3366FF' }} />
                </IconButton>
              </Tooltip>
            )}
            <span>{params?.row?.patientName}</span>
          </span>
        );
      },
    },
    {
      field: 'guardianName',
      headerName: 'Guardian Name',
      width: 200,
      type: 'string',
      sortable: true,
      filterable: true,
    },

    {
      field: 'requestType',
      headerName: 'Request Type',
      width: 150,
      type: 'string',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => {
        return <>{params?.row?.requestType}</>;
      },
    },
    {
      field: 'requestDate',
      headerName: 'Date of Request',
      width: 140,
      type: 'date',
      sortable: true,
      filterable: true,
      filterOperators: DateFilter,
      valueFormatter: (params: any) => dayjs(params?.row?.requestDate).format('MM/DD/YYYY'),
      valueGetter: (params: any) => dayjs(params?.row?.requestDate).format('MM/DD/YYYY'),
      renderCell: (params: any) => {
        return <>{dayjs(params?.row?.requestDate).format('MM/DD/YYYY')}</>;
      },
    },
    {
      field: 'status',
      width: 160,
      type: 'string',
      headerName: 'Status',
      sortable: true,
      filterable: true,
      renderCell: (params: any) => {
        return (
          <>
            <Typography
              sx={{
                background:
                  params?.row?.status === 'Rejected' ||
                  params?.row?.status === 'Update Failed' ||
                  params?.row?.status === 'Transfer Failed'
                    ? '#FFFBFA'
                    : params?.row?.status === 'Approved' ||
                      params?.row?.status === 'Update Successful' ||
                      params?.row?.status === 'Transfer Successful'
                    ? '#F6FEF9'
                    : '#F5F8FF',

                color:
                  params?.row?.status === 'Rejected' ||
                  params?.row?.status === 'Update Failed' ||
                  params?.row?.status === 'Transfer Failed'
                    ? '#B42318'
                    : params?.row?.status === 'Approved' ||
                      params?.row?.status === 'Update Successful' ||
                      params?.row?.status === 'Transfer Successful'
                    ? '#027A48'
                    : '#1570EF',

                border:
                  params?.row?.status === 'Rejected' ||
                  params?.row?.status === 'Update Failed' ||
                  params?.row?.status === 'Transfer Failed'
                    ? '1px solid #B42318'
                    : params?.row?.status === 'Approved' ||
                      params?.row?.status === 'Update Successful' ||
                      params?.row?.status === 'Transfer Successful'
                    ? '1px solid #027A48'
                    : '1px solid #1570EF',

                borderRadius: '12px',
                padding: '2px 10px 0px 10px',
              }}
            >
              {params?.row?.status}
            </Typography>
          </>
        );
      },
    },
  ];

  const rowSelect = (params: any) => {
    if (
      params.row.requestType === 'Change Email' ||
      params.row.requestType === 'Change Phone Number' ||
      params.row.requestType === 'Change Email & Phone Number'
    ) {
      navigate(ROUTE.ADMIN_UPDATE_EMAIL_PHONE, { state: { ticketId: params.row.ticketId, status: params.row.status } });
    } else if (params.row.requestType === 'Transfer Ownership') {
      // eslint-disable-next-line max-len
      navigate(ROUTE.ADMIN_TRANSFER_ACCOUNT_OWNERSHIP, {
        state: { ticketId: params.row.ticketId, status: params.row.status },
      });
    } else if (params.row.requestType === 'Deactivate Account') {
      navigate(ROUTE.ADMIN_DEACTIVATE_ACCOUNT, { state: { ticketId: params.row.ticketId, status: params.row.status } });
    }
  };

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Account Management Requests
        </Typography>
      </Grid>

      <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
        <CustomDataGrid
          centered
          rows={requests ? requests : []}
          columns={columns}
          dashBordKey={pageKey}
          onRowSelect={rowSelect}
          userId={user.userId}
          setting={{ disableExport: !user?.permissions.includes('EXPORT_DATA') }}
        />
      </Grid>
    </Container>
  );
};
export default AccountManagementRequest;
